import { Component, computed, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';

import { CampaignWithRace } from '../../base/campaign-race';

import { AuthService } from '../../auth/auth.service';
import { EncounterService } from '../../services/encounter.service';
import { InformationRaceService } from '../../services/information-race.service';
import { MessageService } from '../../services/message.service';
import { NegotiationService } from '../../services/negotiation.service';
import { UserService } from '../../services/user.service';

import { Encounter } from '../../interfaces/encounter';
import { InformationRace } from '../../interfaces/information-race';
import { Message } from '../../interfaces/message';
import { Negotiation } from '../../interfaces/negotiation';
import { Race } from '../../interfaces/race';
import { User } from '../../interfaces/user';

@Component({
  selector: 'message-base',
  templateUrl: './message-base.component.html',
  standalone: false
})
export class MessageBaseComponent extends CampaignWithRace implements OnInit {
  authService = inject(AuthService);
  encounterService = inject(EncounterService);
  informationRaceService = inject(InformationRaceService);
  messageService = inject(MessageService);
  negotiationService = inject(NegotiationService);
  userService = inject(UserService);

  channel: string = "Notification";
  page: number = 1;
  priority: WritableSignal<number> = signal(0);
  userId = computed<string>(() => this.authService.getUser()._id);
  campaignId = computed<string>(() => this.session.getCampaign()._id);

  // externallyReceived: 0,
  displayUsers: boolean = false;
  users: WritableSignal<User[]> = signal(undefined);
  usersById: { [key: string]: User; } = {};
  selectedUserId: WritableSignal<string> = signal(undefined);

  displayRaces: boolean = false;
  races: WritableSignal<Race[]> = signal(undefined);
  racesToDisplay: Race[] = [];
  racesById: { [key: string]: Race; } = {};
  // informationRaceByRaceId: { [key: string]: InformationRace; } = {};
  friendlyRaceByRaceId: { [key: string]: Race; } = {};
  selectedRaceId: WritableSignal<string> = signal(undefined);

  showOld: WritableSignal<boolean> = signal(false);

  displayNegotiations: boolean = false;
  currentNegotiations: WritableSignal<Negotiation[]> = signal(undefined);
  oldNegotiations: WritableSignal<Negotiation[]> = signal(undefined);
  selectedNegotiationId: WritableSignal<string> = signal(undefined);

  displayEncounters: boolean = false;
  currentEncounters: WritableSignal<Encounter[]> = signal(undefined);
  oldEncounters: WritableSignal<Encounter[]> = signal(undefined);
  selectedEncounterId: WritableSignal<string> = signal(undefined);

  getMore: boolean = false;
  canPost: boolean = false;
  displayMessages: boolean;
  messages: WritableSignal<Message[]> = signal([]);
  filteredMessages = computed<Message[]>(() => {
    return this.messages();
  });

  messageText: any;
  savedMessageId: string;
  externallyReceived: number;

  constructor () {
    super();
  };

  ngOnInit () {
    console.log('channel: ', this.channel);
    this.displayUsers = this.channel === "Player";
    this.displayRaces = this.channel === "Race" ||
      this.channel === "Negotiation" ||
      this.channel === "Encounter";

    // find users for left selection and message prefix
    if (this.displayUsers || this.channel === "Chat") {
      this.session.iAmBusy();
      this.userService.getAllUsers().subscribe(users => {
        this.usersById = users.reduce(
          (hash, user) => (hash[user._id] = user, hash), {}
        );
        this.users.set(users);
        this.session.iAmNotBusy();
      });
    }

    // find all Races for display and selection
    if (this.displayRaces) {
      this.session.iAmBusy();
      this.informationRaceService.getAllInformationRaceForRace(this.session.race._id).subscribe(irs => {
        // this.informationRaceByRaceId = {};
        this.friendlyRaceByRaceId = {};
        let races: Race[] = [];
        for (const informationRace of irs) {
          let race = informationRace.aboutRace;
          this.racesById[race._id] = race;
          // this.informationRaceByRaceId[race._id] = informationRace;
          if (informationRace.politicalStateNumber > 3) {
            this.friendlyRaceByRaceId[race._id] = race;
          }
        }
        this.races.set(races);
        this.racesToDisplay = Object.values(this.friendlyRaceByRaceId);
        this.session.iAmNotBusy();
      });
    }

    // find unsettled "Negotiation"s for display and selection
    if (this.channel === "Negotiation") {
      this.displayRaces = false;
      this.displayNegotiations = true;
      this.session.iAmBusy();
      this.negotiationService.getNegotiationsForCampaign(this.session.campaign).subscribe(
        negotiations => {
          let currentNegotiations = [];
          let oldNegotiations = [];
          for (const negotiation of negotiations) {
            if (negotiation.status === "Complete") {
              oldNegotiations.push(negotiation);
            }
            else {
              currentNegotiations.push(negotiation);
            }
          }
          this.currentNegotiations.set(currentNegotiations);
          this.oldNegotiations.set(oldNegotiations);
          this.session.iAmNotBusy();
        }
      );
    }

    // find unsettled "Encounter"s for display and selection
    if (this.channel === "Encounter") {
      this.displayRaces = false;
      this.displayEncounters = true;
      this.session.iAmBusy();
      this.encounterService.getEncountersForRaceId$(this.session.race._id).subscribe(
        encounters => {
          let currentEncounters = [];
          let oldEncounters = [];
          for (const encounter of encounters) {
            if (encounter.status === "Complete") {
              oldEncounters.push(encounter);
            }
            else {
              currentEncounters.push(encounter);
            }
          }
          this.currentEncounters.set(currentEncounters);
          this.oldEncounters.set(oldEncounters);
          this.session.iAmNotBusy();
        }
      );
    }

    this.displayMessages = (this.channel === "Notification" || this.channel === "Chat");
  };

  // let messageData = {
  //     campaignId: campaignId,
  //     channel: channel,
  //     message: messageText,
  //     userIdFrom: userId,
  //     raceIdFrom: raceIdFrom,
  //     raceIdTo: raceIdTo,
  //     priority: priority,
  //     userIdTo: userIdTo,
  //     identity: identity
  // };

  addMessage () {
    let newMessage = this.messageService.buildMessageTemplate(
      this.session.campaign._id,
      this.channel,
      this.messageText,
      this.userId(),
      this.selectedUserId(),
      this.session.race._id,
      this.selectedRaceId(),
      this.selectedNegotiationId(),
      this.selectedEncounterId()
    );
    this.messageService.saveMessage(newMessage).subscribe(
      (savedMessage: Message) => {
        this.savedMessageId = savedMessage._id;
        this.messageService.sendMessage(savedMessage);
        this.displayMessages = true;
      }
    );
  };

  resetMessageDisplay () {
    this.page = 1;
    this.externallyReceived = 0;
    // this.messages = [];
    this.getMore = false;
    this.canPost = true;
  };

  getMessages (channel: string, id1: string, id2: string = undefined) {
    console.log('Getting messages for channel: %s, id1: %s, id2: %s', channel, id1, id2);
    this.session.iAmBusy();
    this.messageService.getMessages(this.campaignId(), channel, id1, id2, this.page, this.externallyReceived).subscribe(
      messages => {
        this.messages.set(messages);
        this.session.iAmNotBusy();
      }
    );
  };

  getUserMessages (otherUserId: string) {
    this.resetMessageDisplay();
    this.getMessages('Player', this.userId(), otherUserId);
  };

  getRaceMessages (otherRaceId) {
    this.resetMessageDisplay();
    this.getMessages('Race', this.userId(), otherRaceId);
  };

  getNegotiationMessages (negotiationId: string) {
    this.resetMessageDisplay();
    this.getMessages('Negotiation', negotiationId);
  };

  getEncounterMessages (encounterId: string) {
    this.resetMessageDisplay();
    this.getMessages('Encounter', encounterId);
  };

  // message sent over webSocket:
  // args = {
  //     campaignId: '610d998d9c02a207c6719748',
  //     channel: 'Chat',
  //     message: 'This is a general test!',
  //     userIdFrom: '5cf423ed10248f25c9122750',
  //     raceIdFrom: '610dc6a09c02a207c6719bb3'
  // }
  // $scope.$on (this.channel, function (event, sentMessage) {
  //     console.log('recieving:', sentMessage); // args is really the message
  //     this.messages.push(sentMessage);
  //     this.externallyReceived++;
  //     if (this.savedMessageId === sentMessage._id) {
  //       // clear data to acknowlege receipt of message
  //       this.savedMessageId = null;
  //       this.messageText = null;
  //     }
  //     $timeout(
  //       function () {
  //         return "Done!";
  //       }
  //     );
  //   } );

};
